import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaBuilding, FaCity, FaHome, FaPen, FaSave } from 'react-icons/fa';
import PlacesSearch from 'components/Header/PlacesSearch';
import { MapContainer, TileLayer } from 'react-leaflet';
import Cookies from 'js-cookie';
import LocationSetter from 'components/AlertCreator/components/LocationSetter';
import { FaBuildingColumns, FaMapLocation } from 'react-icons/fa6';
import { RiCloseFill } from 'react-icons/ri';
import { usePerms } from 'contexts/PermsContext';
import { Button, Container, Form } from 'react-bootstrap';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import BaseTable from 'components/Tables/BaseTable';
import { getUserPlacesData, updateUserPlacesData } from 'services/user/places.service';

export const UserPlaces = () => {
  const [places, setPlaces] = useState([]);
  const [mapCenter, setMapCenter] = useState([Cookies.get('latitude'), Cookies.get('longitude')]);
  const [zoom] = useState(11);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);
  const [workingTitle, setWorkingTitle] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState(null);
  const [filterInput, setFilterInput] = useState('');
  const perms = usePerms();

  function typeMap(type) {
    switch (type) {
      case 'locality':
        return 'City';
      case 'administrative_area_level_1':
        return 'State';
      case 'administrative_area_level_2':
        return 'County';
      case 'premise':
        return 'Building';
      case 'country':
        return 'Country';
      case 'geocode':
        return 'Location';
      default:
        return 'Building';
    }
  }

  function typeIcon(type) {
    switch (type) {
      case 'DefaultLocationUserHome':
        return <FaHome size={20} color="white" />;
      case 'locality':
        return <FaCity size={20} color="white" />;
      case 'administrative_area_level_1':
        return <FaBuildingColumns size={20} color="white" />;
      case 'administrative_area_level_2':
        return <FaBuildingColumns size={20} color="white" />;
      case 'premise':
        return <FaBuilding size={20} color="white" />;
      case 'geocode':
        return <FaMapLocation size={20} color="white" />;
      default:
        return <FaBuilding size={20} color="white" />;
    }
  }

  useEffect(() => {
    (async () => {
      const response = await getUserPlacesData();
      const placeList = response.data.places || [];
      setPlaces(placeList);
      if (placeList.length > 0) {
        setMapCenter([placeList[0].latitude, placeList[0].longitude]);
        setSelectedPlace(placeList[Cookies.get('selectedLocationIndex')]);
      }
    })();
  }, []);

  const addLocation = (location) => {
    setMapCenter([location.latitude, location.longitude]);
    setSelectedLocation(location);
  };

  const saveLocation = (location) => {
    if (places.find((place) => place.place_id === location.place_id)) return;
    let tempPlaces = [...places];
    tempPlaces.push(location);
    tempPlaces.sort((a, b) => {
      if (a.DefaultLocationUserHome === true) return -1;
      if (b.DefaultLocationUserHome === true) return 1;
      if (a.title > b.title) return 1;
      if (a.title < b.title) return -1;
      return 0;
    });
    setPlaces([...tempPlaces]);
    updateUserPlacesData([...tempPlaces]);
  };

  const editLocationTitle = useCallback(
    (location, title) => {
      let tempPlaces = [...places];
      let index = tempPlaces.indexOf(location);
      if (index > -1) {
        tempPlaces[index].title = title;
      }
      setPlaces([...tempPlaces]);
      updateUserPlacesData([...tempPlaces]);
    },
    [places],
  );

  const changeHomeLocation = useCallback(
    (location) => {
      let tempPlaces = [...places];
      tempPlaces = tempPlaces.map((place) => {
        place.DefaultLocationUserHome = false;
        return place;
      });
      let index = tempPlaces.indexOf(location);
      if (index > -1) {
        tempPlaces[index].DefaultLocationUserHome = true;
      }
      tempPlaces = tempPlaces.sort((a, b) => {
        if (a.DefaultLocationUserHome === true) return -1;
        if (b.DefaultLocationUserHome === true) return 1;
        if (a.title > b.title) return 1;
        if (a.title < b.title) return -1;
        return 0;
      });

      setEditIndex(-1);
      setPlaces([...tempPlaces]);
      updateUserPlacesData([...tempPlaces]);
      setSelectedPlace([...tempPlaces][0]);
      Cookies.set('selectedLocationIndex', 0);
    },
    [places],
  );

  useEffect(() => {
    if (selectedPlace) {
      setMapCenter([selectedPlace.latitude, selectedPlace.longitude]);
      setSelectedLocation(selectedPlace);
      Cookies.set('city', selectedPlace.city);
      Cookies.set('state', selectedPlace.state_long);
      Cookies.set('latitude', selectedPlace.latitude);
      Cookies.set('longitude', selectedPlace.longitude);
    }
  }, [selectedPlace]);

  const removeLocation = (location) => {
    if (places.length === 1) return;
    let tempPlaces = [...places];
    let index = tempPlaces.indexOf(location);
    if (index > -1) {
      tempPlaces.splice(index, 1);
    }
    setPlaces([...tempPlaces]);
    updateUserPlacesData([...tempPlaces]);
    setSelectedPlace([...tempPlaces][0]);
    Cookies.set('selectedLocationIndex', 0);
  };

  const columns = useMemo(() => {
    return [
      {
        header: 'Title',
        accessorKey: 'title',
        Cell: ({ row, cell }) => {
          const title = cell.getValue();
          const typeIcon = row.original.typeIcon;
          const index = row.original.index;
          const place = row.original.place;

          return (
            <div className="d-flex align-items-center gap-2">
              <div className="rounded-circle d-flex justify-content-center align-items-center p-2 border border-2 border-white bg-primary-shadow">
                {typeIcon}
              </div>
              {editIndex !== index ? (
                <p className="text-white fs-6">{title}</p>
              ) : (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    editLocationTitle(place, workingTitle);
                    setEditIndex(-1);
                  }}
                >
                  <Form.Control
                    value={workingTitle}
                    onChange={(e) => {
                      setWorkingTitle(e.target.value);
                    }}
                    className="w-100 rounded-2"
                    style={{ border: '1px solid white' }}
                  ></Form.Control>
                </Form>
              )}
            </div>
          );
        },
        size: 180,
      },
      {
        header: 'Type',
        accessorKey: 'type',
        Cell: ({ row, cell }) => {
          const type = cell.getValue();
          return <p className="text-white fs-6">{type}</p>;
        },
        size: 100,
      },
      {
        header: 'Location',
        accessorKey: 'location',
        Cell: ({ row, cell }) => {
          const location = cell.getValue();
          return <p className="text-white fs-6">{location}</p>;
        },
        size: 130,
      },
      {
        header: '',
        accessorKey: 'actions',
        Cell: ({ row, cell }) => {
          const place = row.original.place;
          const index = row.original.index;

          return (
            <div className="cursor-pointer d-flex justify-content-end align-items-center gap-2">
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                editIndex === index && (
                  <div
                    onClick={() => {
                      editLocationTitle(place, workingTitle);
                      setEditIndex(-1);
                    }}
                  >
                    <FaSave color="white" />
                  </div>
                )}
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                editIndex !== index && (
                  <div
                    onClick={() => {
                      setEditIndex(index);
                      setWorkingTitle(place.title || '');
                    }}
                  >
                    <FaPen color="white" />
                  </div>
                )}
              {!place.DefaultLocationUserHome &&
                perms.testPermission([8, 9, 10, 11, 12, 13], ['HomeLocationEditable']) && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      changeHomeLocation(place);
                    }}
                  >
                    <FaHome color="white" />
                  </div>
                )}
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                !place.DefaultLocationUserHome && (
                  <div
                    onClick={() => {
                      setPlaceToDelete(place);
                      setShowConfirmDelete(true);
                    }}
                  >
                    <RiCloseFill size={25} color="white" />
                  </div>
                )}
            </div>
          );
        },
        enableSorting: false,
        size: 180,
      },
    ];
  }, [changeHomeLocation, editIndex, perms, workingTitle, editLocationTitle]);

  const rows = useMemo(() => {
    return places.map((place, index) => ({
      id: place.place_id,
      title: place.title,
      typeIcon: typeIcon(place.type),
      type: typeMap(place.type),
      location: place.place_data?.formatted_address,
      place: place,
      index: index,
    }));
  }, [places]);

  return (
    <Container fluid className="page-scroll-area mt-3">
      {showConfirmDelete && (
        <ConfirmationModal
          show={showConfirmDelete}
          handleShow={() => setShowConfirmDelete(false)}
          bodyTxt={`Are you sure you want to delete this place?`}
          cancelBtnTxt="No"
          confirmBtnTxt="Yes"
          confirmHandler={() => {
            removeLocation(placeToDelete);
            setShowConfirmDelete(false);
          }}
        />
      )}
      <div className="d-flex flex-column flex-xl-row h-100" style={{ gap: 20 }}>
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) && (
          <div className="d-flex flex-column rounded-3 p-2 w-100 bg-base h-max">
            <div className="d-flex flex-column flex-md-row justify-content-between mb-2">
              <div className="fw-bold fs-5">Add a Place</div>
              <PlacesSearch
                noBack
                noAutoAdd
                places={places}
                setSearching={() => {}}
                addLocation={addLocation}
                setMapCenter={setMapCenter}
                quickSave
              />
            </div>
            {/* Map Container - height is 75% of the width */}
            <div className="ratio ratio-4x3 w-100" style={{ background: 'grey' }}>
              <MapContainer center={mapCenter} zoom={11} style={{ height: '100%', width: '100%' }}>
                <LocationSetter center={mapCenter} zoom={zoom} />
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                />
              </MapContainer>
            </div>
            <div className="d-flex flex-column">
              {selectedLocation && (
                <div className="p-1 mt-1">{selectedLocation.place_data?.formatted_address}</div>
              )}
              <Button
                onClick={() => {
                  saveLocation(selectedLocation);
                }}
                className="mt-2 p-2 cursor-pointer rounded-2 w-max"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        )}
        <div className="d-flex flex-column w-100 p-2 rounded-3">
          <div className="d-flex justify-content-between flex-column flex-md-row">
            <div className="fw-bold fs-5">Saved Places</div>
            <Form.Control
              className="p-2 mb-2 rounded-4 w-auto border-0 bg-primary-hightlight"
              placeholder="Search"
              value={filterInput}
              onChange={(e) => {
                setFilterInput(e.target.value);
              }}
            ></Form.Control>
          </div>
          <BaseTable
            columns={columns}
            rows={rows}
            selectedRowId={selectedPlace?.place_id}
            customStyles={{ cell: { borderBottom: '1px solid #56577A' } }}
            altRowSettings={{
              alternate: true,
              color1: '#2C373B',
              color2: '#222A2D',
            }}
            onRowClick={(row) => {
              setSelectedPlace(row.place);
              Cookies.set('selectedLocationIndex', row.index);
            }}
          />
        </div>
      </div>
    </Container>
  );
};
